import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AiOutlineArrowLeft } from 'react-icons/ai';
import apiClient from '../utils/apiClient';
import { grantAdminRole, revokeAdminRole, listAdmins, totalUsersRoute } from '../utils/APIRoutes';
import { useServerRole } from '../utils/permissions';

// This screen didn't exist at all before — the backend has supported
// granting/revoking limited admin roles by email for a while now, but
// nothing in the UI called it. Reachable from an admin's own nav; not
// linked from anywhere public.
const ALL_PERMISSIONS = [
  { key: 'manage_users', label: 'Manage users (allow/reject sellers)' },
  { key: 'manage_products', label: 'Manage products (moderate/delete any)' },
  { key: 'send_emails', label: 'Send bulk emails' },
  { key: 'view_all_orders', label: 'View all orders' },
  { key: 'manage_orders', label: 'Manage orders (mark processed/shipped, delete, release funds, reject)' },
  { key: 'manage_subscriptions', label: 'Manage subscriptions' },
  { key: 'view_insights', label: 'View insights/analytics dashboard' },
  { key: 'manage_agents', label: 'Manage delivery agents' },
];

const AdminManagement = () => {
  const { isAdmin, hasPermission, loading: roleLoading } = useServerRole();

  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [email, setEmail] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [statusMessage, setStatusMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [totalUsers, setTotalUsers] = useState(null);
  const [loadingTotal, setLoadingTotal] = useState(true);
  const [totalError, setTotalError] = useState('');

  const canManageAdmins = isAdmin && hasPermission('manage_users');

  const fetchAdmins = useCallback(async () => {
    setLoadingAdmins(true);
    try {
      const { data } = await apiClient.get(listAdmins);
      setAdmins(data.admins || []);
    } catch (error) {
      console.error('Error listing admins:', error);
    } finally {
      setLoadingAdmins(false);
    }
  }, []);

  const fetchTotalUsers = useCallback(async () => {
  setLoadingTotal(true);
  setTotalError('');
  try {
    const { data } = await apiClient.get(totalUsersRoute);
    // Endpoint returns { success: true, totalUsers: <number> }
    setTotalUsers(data.success ? data.totalUsers : null);
  } catch (error) {
    console.error('Error fetching total users:', error);
    setTotalError(error.response?.data?.msg || 'Could not load total users');
  } finally {
    setLoadingTotal(false);
  }
}, []);

  useEffect(() => {
  if (canManageAdmins) {
    fetchAdmins();
    fetchTotalUsers();
  }
}, [canManageAdmins, fetchAdmins, fetchTotalUsers]);

  const togglePermission = (key) => {
    setSelectedPermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  const handleGrant = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setStatusMessage({ type: 'error', text: 'Enter an email address.' });
      return;
    }
    if (selectedPermissions.length === 0) {
      setStatusMessage({ type: 'error', text: 'Select at least one permission.' });
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await apiClient.post(grantAdminRole, {
        email: trimmedEmail,
        permissions: selectedPermissions,
      });
      setStatusMessage({ type: 'success', text: data.msg });
      setEmail('');
      setSelectedPermissions([]);
      fetchAdmins();
    } catch (error) {
      setStatusMessage({
        type: 'error',
        text: error.response?.data?.msg || 'Error granting admin role',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (targetEmail) => {
    if (!window.confirm(`Revoke admin access for ${targetEmail}?`)) return;

    try {
      const { data } = await apiClient.post(revokeAdminRole, { email: targetEmail });
      setStatusMessage({ type: 'success', text: data.msg });
      fetchAdmins();
    } catch (error) {
      setStatusMessage({
        type: 'error',
        text: error.response?.data?.msg || 'Error revoking admin role',
      });
    }
  };

  if (roleLoading) {
    return <p style={{ textAlign: 'center', padding: '2rem' }}>Loading...</p>;
  }

  if (!canManageAdmins) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Link to={'/'}>
          <AiOutlineArrowLeft style={{ position: 'absolute', top: 8, left: 8, fontSize: 24, cursor: 'pointer' }} />
        </Link>
        <h2>Access Denied</h2>
        <p>You don't have permission to manage admin roles.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto', padding: '2rem 1rem' }}>
      <Link to={'/'}>
        <AiOutlineArrowLeft style={{ position: 'absolute', top: 8, left: 8, fontSize: 24, cursor: 'pointer' }} />
      </Link>

      <h1 style={{ fontSize: '20px', marginBottom: '1rem' }}>Admin Roles</h1>
      <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            background: '#f9fafb',
          }}
        >
          <div>
            <div style={{ fontSize: '12px', color: '#666' }}>Total registered users</div>
            <div style={{ fontSize: '24px', fontWeight: 700 }}>
              {loadingTotal
                ? '...'
                : totalError
                ? '—'
                : totalUsers?.toLocaleString() ?? '—'}
            </div>
            {totalError && (
              <div style={{ fontSize: '11px', color: '#dc2626' }}>{totalError}</div>
            )}
          </div>

          <button
            type="button"
            onClick={fetchTotalUsers}
            disabled={loadingTotal}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              background: 'white',
              cursor: loadingTotal ? 'default' : 'pointer',
              fontSize: '12px',
            }}
          >
            Refresh
          </button>
        </div>

      {statusMessage && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px',
            color: 'white',
            backgroundColor: statusMessage.type === 'error' ? '#dc2626' : '#059669',
          }}
        >
          {statusMessage.text}
        </div>
      )}

      <form onSubmit={handleGrant} style={{ marginBottom: '2rem', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
        <h2 style={{ fontSize: '16px', marginBottom: '12px' }}>Grant limited admin access</h2>

        <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px' }}>Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          required
          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', marginBottom: '14px' }}
        />

        <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px' }}>Permissions</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
          {ALL_PERMISSIONS.map(({ key, label }) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
              <input
                type="checkbox"
                checked={selectedPermissions.includes(key)}
                onChange={() => togglePermission(key)}
              />
              {label}
            </label>
          ))}
        </div>

        <button
          type="submit"
          disabled={submitting}
          style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#2563eb', color: 'white', cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1 }}
        >
          {submitting ? 'Granting...' : 'Grant Access'}
        </button>
      </form>

      <h2 style={{ fontSize: '16px', marginBottom: '12px' }}>Current admins</h2>

      {loadingAdmins ? (
        <p>Loading...</p>
      ) : admins.length === 0 ? (
        <p style={{ color: '#666' }}>No admins found.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {admins.map((admin) => (
            <li
              key={admin._id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '10px 12px',
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>
                  {admin.username} — {admin.email}
                  {admin.role === 'superadmin' && (
                    <span style={{ marginLeft: '8px', fontSize: '11px', color: '#7c3aed' }}>SUPERADMIN</span>
                  )}
                </div>
                {admin.role === 'admin' && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {(admin.adminPermissions || []).join(', ') || 'no permissions'}
                  </div>
                )}
              </div>

              {admin.role !== 'superadmin' && (
                <button
                  onClick={() => handleRevoke(admin.email)}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontSize: '12px' }}
                >
                  Revoke
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminManagement;
